/**
 * TD Multi Offer - Product Tab Web Component
 *
 * Main container for a product offering. Handles:
 * - Variant selection and tracking
 * - Offer selection (selling plans, deals)
 * - Quantity management
 * - Add to cart functionality
 *
 * @listens change - From variant picker and offer inputs
 */

if (!customElements.get('td-mo-product-tab')) {
  class TdMoProductTab extends HTMLElement {
    constructor() {
      super();
      this.productData = null;
      this.selectedVariantId = null;
      this.selectedOffer = null;
    }

    /**
     * Initializes the component when connected to the DOM
     */
    connectedCallback() {
      this.initializeProductData();
      this.initializeElements();
      this.setupEventListeners();
      this.initializeSelectedVariant();
      this.initializeSelectedOffer();
    }

    /**
     * Parses and stores product data from embedded JSON
     */
    initializeProductData() {
      const script = this.querySelector('.td-mo-tab__product-data');
      if (script) {
        try {
          this.productData = JSON.parse(script.textContent);
        } catch (e) {
          console.warn('Failed to parse product data:', e);
        }
      }
    }

    /**
     * Queries and stores references to key elements
     */
    initializeElements() {
      this.variantPicker = this.querySelector('[data-variant-picker]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.addToCartButton = this.querySelector('[data-mo-add-to-cart]');
    }

    /**
     * Sets up event listeners
     */
    setupEventListeners() {
      // Variant picker changes
      if (this.variantPicker) {
        this.variantPicker.addEventListener('change', this.handleVariantChange.bind(this));
      }

      // Offer selection changes
      this.addEventListener('change', this.handleOfferChange.bind(this));

      // Add to cart click
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
      }
    }

    /**
     * Initializes the selected variant from the button's data attribute
     */
    initializeSelectedVariant() {
      if (this.addToCartButton) {
        this.selectedVariantId = this.addToCartButton.dataset.variantId;
      }
    }

    /**
     * Initializes with the first checked offer
     */
    initializeSelectedOffer() {
      const allOffers = Array.from(this.querySelectorAll('input[data-mo-offer]'));
      if (!allOffers.length) return;

      const checkedOffers = allOffers.filter((offer) => offer.checked);
      const selectedOffer = checkedOffers[0] || allOffers[0];
      this.enforceSingleOfferSelection(selectedOffer);
      this.updateSelectedOffer(selectedOffer);
    }

    /**
     * Handles variant picker changes
     * @param {Event} event - The change event
     */
    handleVariantChange(event) {
      if (!this.productData) return;

      const selectedOptions = this.getSelectedOptions();
      const matchingVariant = this.findMatchingVariant(selectedOptions);

      if (matchingVariant) {
        this.selectedVariantId = matchingVariant.id.toString();

        // Update the add to cart button's variant ID
        if (this.addToCartButton) {
          this.addToCartButton.dataset.variantId = this.selectedVariantId;
        }

        // Update disabled states for other options
        this.updateOptionAvailability(matchingVariant);

        // Emit variant changed event
        this.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variant: matchingVariant }
        }));
      }
    }

    /**
     * Gets currently selected option values
     * @returns {Array} Array of selected values
     */
    getSelectedOptions() {
      const options = [];
      if (!this.variantPicker) return options;

      const fieldsets = this.variantPicker.querySelectorAll('fieldset');
      fieldsets.forEach(fieldset => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) {
          options.push(checked.value);
        }
      });
      return options;
    }

    /**
     * Finds variant matching selected options
     * @param {Array} selectedOptions - Array of selected option values
     * @returns {Object|null}
     */
    findMatchingVariant(selectedOptions) {
      if (!this.productData || !this.productData.variants) return null;

      return this.productData.variants.find(variant => {
        return variant.options.every((option, index) => {
          return selectedOptions[index] === option;
        });
      });
    }

    /**
     * Updates option availability based on current selection
     * @param {Object} currentVariant - The currently selected variant
     */
    updateOptionAvailability(currentVariant) {
      // This could be expanded to show/hide unavailable combinations
      // For now, we just track the selected variant
    }

    /**
     * Handles offer radio input changes
     * @param {Event} event - The change event
     */
    handleOfferChange(event) {
      const target = event.target;
      if (target.matches('input[data-mo-offer]')) {
        this.enforceSingleOfferSelection(target);
        this.updateSelectedOffer(target);
      }
    }

    enforceSingleOfferSelection(selectedInput) {
      if (!selectedInput) return;

      const allOffers = this.querySelectorAll('input[data-mo-offer]');
      allOffers.forEach((offerInput) => {
        offerInput.checked = offerInput === selectedInput;
      });
    }

    /**
     * Updates the selected offer
     * @param {HTMLInputElement} offerInput - The selected offer radio input
     */
    updateSelectedOffer(offerInput) {
      this.selectedOffer = {
        id: offerInput.value,
        variantId: offerInput.dataset.variantId,
        sellingPlanId: offerInput.dataset.sellingPlanId,
        deal: offerInput.dataset.deal
      };

      // Emit offer selected event
      this.dispatchEvent(new CustomEvent('offer-selected', {
        bubbles: true,
        detail: this.selectedOffer
      }));
    }

    /**
     * Handles add to cart button click
     * @param {Event} event - The click event
     */
    handleAddToCart(event) {
      event.preventDefault();

      const variantId = this.selectedVariantId || this.addToCartButton.dataset.variantId;
      if (!variantId) {
        console.warn('No variant selected');
        return;
      }

      const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;

      const cartData = {
        id: variantId,
        quantity: quantity
      };

      // Add selling plan from offer if present
      if (this.selectedOffer && this.selectedOffer.sellingPlanId) {
        cartData.selling_plan = this.selectedOffer.sellingPlanId;
      }

      // Add properties if deal is set
      if (this.selectedOffer && this.selectedOffer.deal) {
        cartData.properties = {
          'Deal': this.selectedOffer.deal
        };
      }

      this.addToCart(cartData);
    }

    /**
     * Adds item to cart via AJAX
     * @param {Object} cartData - The cart data to add
     */
    addToCart(cartData) {
      const button = this.addToCartButton;
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      button.classList.add('loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Add to cart failed');
          }
          return response.json();
        })
        .then(data => {
          button.textContent = 'Added!';

          // Check if we should skip cart
          const section = this.closest('td-multi-offer');
          const skipCart = section?.dataset.skipCart === 'true';

          if (skipCart) {
            window.location.href = '/checkout';
          } else {
            // Trigger cart update event
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

            // Reset button after delay
            setTimeout(() => {
              button.textContent = originalText;
              button.classList.remove('loading');
            }, 1500);
          }
        })
        .catch(error => {
          console.error('Add to cart error:', error);
          button.textContent = 'Error';
          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('loading');
          }, 2000);
        });
    }
  }

  customElements.define('td-mo-product-tab', TdMoProductTab);
}
